import { useEffect, useRef, useState } from "react";
import FilterTag from "../../tag/FilterTag";
import { useSearchParams } from "react-router-dom";
import { useFilterDataQuery } from "../../config/useFilterDataQuery";
import { useLanguage } from "../../../../../shared/hooks/useLanguageContext";
import {
  createMultilingualProduct,
  createMultilingualKeyword,
} from "../../../../../shared/utils/multilingual-helpers";

type FilteredSearchComponentProps = {
  onReset: () => void;
};

export default function FilteredSearchComponent({
  onReset,
}: FilteredSearchComponentProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filteredTags, setFilteredTags] = useState<
    { type: string; id: string; name: string }[]
  >([]);
  const [isFixed, setIsFixed] = useState(false);
  const [dimensions, setDimensions] = useState({ width: 0, left: 0 });
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const filterDataQuery = useFilterDataQuery();

  // 다국어 지원을 위한 훅
  const { getLocalizedText } = useLanguage();

  useEffect(() => {
    const updateDimensions = () => {
      if (wrapperRef.current) {
        const parent = wrapperRef.current.parentElement;
        if (parent) {
          const { width, left } = parent.getBoundingClientRect();
          setDimensions({ width, left });
        }
      }
    };

    const handleScroll = () => {
      const scrollY = window.scrollY;
      const threshold = 150; // 특정 스크롤 위치 설정

      if (scrollY > threshold) {
        setIsFixed(true);
      } else {
        setIsFixed(false);
      }
    };

    // 초기 크기 설정 및 리사이즈 감지
    updateDimensions();
    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  useEffect(() => {
    if (
      !filterDataQuery.productsQuery.data ||
      !filterDataQuery.keywordsQuery.data ||
      !filterDataQuery.uxEvaluationsQuery.data
    ) {
      return;
    }

    const products = filterDataQuery.productsQuery.data;
    const keywords = filterDataQuery.keywordsQuery.data.flatMap(
      (category) => category.keywords,
    );
    const uxEvaluations = filterDataQuery.uxEvaluationsQuery.data;

    const allProductIds = searchParams.getAll("product");
    const allKeywordIds = searchParams.getAll("keyword");
    const allEvaluations = searchParams.getAll("uxEvaluation");

    const selectedProducts = allProductIds
      .map((id) => {
        const product = products?.find((p) => `product_${p.id}` === id);
        if (product) {
          const multilingualProduct = createMultilingualProduct({
            nameKo: product.nameKo,
            nameEn: product.nameEn,
          });
          return {
            type: "product",
            id: product.id,
            name: getLocalizedText(multilingualProduct.name),
          };
        }
        return null;
      })
      .filter(Boolean);

    const selectedKeywords = allKeywordIds
      .map((id) => {
        const keyword = keywords?.find((k) => `keyword_${k.id}` === id);
        if (keyword) {
          const multilingualKeyword = createMultilingualKeyword({
            nameKo: keyword.nameKo,
            nameEn: keyword.nameEn,
            descKo: keyword.descKo,
            descEn: keyword.descEn,
            categoryNameKo: "",
            categoryNameEn: "",
          });
          return {
            type: "keyword",
            id: keyword.id,
            name: getLocalizedText(multilingualKeyword.name),
          };
        }
        return null;
      })
      .filter(Boolean);

    const selectedEvaluations = allEvaluations
      .map((id) => {
        // uxEvaluations는 단일 객체이므로 직접 처리
        if (!uxEvaluations) return null;

        // URL 파라미터에서 GOOD 또는 BAD를 추출
        const isGood = id === "uxEvaluation_GOOD";
        const isBad = id === "uxEvaluation_BAD";

        // GOOD인 경우 goodUxCount가 0보다 큰지 확인
        if (isGood && uxEvaluations.goodUxCount > 0) {
          return {
            type: "uxEvaluation",
            id: "GOOD",
            name: "Good UX",
          };
        }

        // BAD인 경우 badUxCount가 0보다 큰지 확인
        if (isBad && uxEvaluations.badUxCount > 0) {
          return {
            type: "uxEvaluation",
            id: "BAD",
            name: "Bad UX",
          };
        }

        return null;
      })
      .filter(Boolean);

    setFilteredTags([
      ...(selectedProducts.filter(
        (product) => product?.id && product?.name,
      ) as { type: string; id: string; name: string }[]),
      ...(selectedKeywords.filter(
        (keyword) => keyword?.id && keyword?.name,
      ) as { type: string; id: string; name: string }[]),
      ...(selectedEvaluations.filter(
        (evaluation) => evaluation?.id && evaluation?.name,
      ) as { type: string; id: string; name: string }[]),
    ]);
  }, [
    searchParams,
    filterDataQuery.productsQuery.data,
    filterDataQuery.keywordsQuery.data,
    filterDataQuery.uxEvaluationsQuery.data,
  ]);

  const handleRemoveFilter = (type: string, id: string) => {
    const paramName = `${type}_${id}`;
    const updatedParams = searchParams
      .getAll(type)
      .filter((param) => param !== paramName);

    searchParams.delete(type);
    updatedParams.forEach((param) => searchParams.append(type, param));

    setSearchParams(searchParams);
  };

  const resetFilters = () => {
    setFilteredTags([]); // 상태 초기화
    window.history.replaceState({}, "", `${window.location.pathname}`);
    onReset();
  };

  return (
    <div
      ref={wrapperRef}
      className={`filtered-search-wrapper ${isFixed ? "fixed" : ""}`}
      style={
        {
          "--dynamic-width": `${dimensions.width}px`,
          "--dynamic-left": `${dimensions.left}px`,
        } as React.CSSProperties
      }
    >
      <div className="reset-container" onClick={resetFilters}>
        <img alt="reset" src={"/assets/icons/ClockClockwise.svg"} />
        <div>초기화</div>
      </div>
      <div className="filtered-container">
        {filteredTags.map((tag) => (
          <FilterTag
            key={`${tag.type}_${tag.id}`}
            name={tag.name}
            onRemove={() => handleRemoveFilter(tag.type, tag.id)}
          />
        ))}
      </div>
    </div>
  );
}
